import { useParams } from 'react-router-dom';
import { usePopoutMeetingState } from '../hooks/usePopoutMeetingState';
import { VideoTile } from '../components/meeting/VideoTile';

/**
 * Full-page participants view for pop-out window.
 */
export default function ParticipantsPopoutPage() {
  const { roomCode } = useParams<{ roomCode: string }>();
  const { participants, localParticipant, isConnected, isSynced } = usePopoutMeetingState();

  const allParticipants = localParticipant
    ? [localParticipant, ...participants.filter((p) => p.participantId !== localParticipant.participantId)]
    : participants;

  if (!isSynced || !isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-3 border-accent border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">Syncing with meeting...</p>
          <p className="text-sm text-gray-500 mt-2">
            Keep your main meeting tab open
          </p>
        </div>
      </div>
    );
  }

  const gridCols = getGridCols(allParticipants.length);

  return (
    <div className="min-h-screen bg-bg-primary p-4">
      <header className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold text-gray-300">
          Participants ({allParticipants.length})
        </h1>
        <span className="text-sm text-gray-500">{roomCode}</span>
      </header>

      <div className={`grid ${gridCols} gap-3 h-[calc(100vh-80px)]`}>
        {allParticipants.map((participant, index) => (
          <VideoTile
            key={participant.participantId}
            participant={participant}
            isLocal={index === 0 && !!localParticipant}
            enableAudio={false}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Determines grid columns based on participant count.
 */
function getGridCols(count: number): string {
  if (count <= 1) return 'grid-cols-1';
  if (count <= 2) return 'grid-cols-2';
  if (count <= 4) return 'grid-cols-2';
  if (count <= 6) return 'grid-cols-3';
  if (count <= 9) return 'grid-cols-3';
  if (count <= 12) return 'grid-cols-4';
  return 'grid-cols-4';
}
